import React, { useState, useEffect, useRef } from "react";
import { FaPaperclip, FaTimesCircle, FaFile, FaReply } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import "./UserChatbox.css";
import io from "socket.io-client";

const UserChatbox = ({ user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [pendingMessage, setPendingMessage] = useState("");
  const [adminTyping, setAdminTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // New state for reply
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
    const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const [chatboxSize, setChatboxSize] = useState({ width: 380, height: 500 });
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });
  const [isMobile, setIsMobile] = useState(false);

  // Enhanced textarea handling for mobile
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    
    // Clear previous height
    textarea.style.height = 'auto';
    
    // Calculate new height with constraints
    const newHeight = Math.min(textarea.scrollHeight, 120); // Same as maxHeight in style
    const minHeight = 20; // Minimum height for single line
    
    // Ensure minimum height for mobile usability
    textarea.style.height = Math.max(newHeight, minHeight) + 'px';
    
    setNewMessage(textarea.value);
    
    // Send typing indicator to server (throttled for mobile)
    if (!isMobile) {
      // Desktop: send immediately
      if (socketRef.current) {
        socketRef.current.emit('typing', {
          userId: user._id,
          isTyping: textarea.value.length > 0
        });
      }
    } else {
      // Mobile: throttle typing indicator
      clearTimeout(window.typingTimeout);
      window.typingTimeout = setTimeout(() => {
        if (socketRef.current) {
          socketRef.current.emit('typing', {
            userId: user._id,
            isTyping: textarea.value.length > 0
          });
        }
      }, 500);
    }
  };

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;
    
    // Connect to Socket.IO server with query params
    socketRef.current = io(process.env.REACT_APP_API_BASE_URL, {
      query: { userId: user._id }
    });
    
    // Join user-specific room
    socketRef.current.emit('joinRoom', user._id);
    
    // Listen for new messages
    socketRef.current.on('newMessage', (message) => {
      setMessages(prevMessages => [...prevMessages, message]);
    });
    
    // Listen for typing indicators
    socketRef.current.on('adminTyping', ({ isTyping }) => {
      console.log('Admin typing:', isTyping);
      setAdminTyping(isTyping);
    });
    
    // Clean up on unmount
    return () => {
      socketRef.current.disconnect();
    };
  }, [user]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = window.innerWidth <= 768;
      setIsMobile(mobileCheck);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced mobile keyboard and input handling
  useEffect(() => {
    if (!isMobile) return;

    // Set CSS custom property for proper viewport height calculation
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial value
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure viewport is updated after orientation change
      setTimeout(setVH, 100);
    });

    let keyboardVisible = false;

    // Prevent zoom on input focus for iOS
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      const originalContent = viewport.getAttribute('content');

      const handleFocus = () => {
        // Set maximum scale to 1.0 to prevent zooming
        viewport.setAttribute('content', originalContent + ', maximum-scale=1.0');
        keyboardVisible = true;

        // Add keyboard-visible class for CSS adjustments
        const footer = document.querySelector('.user-chatbox-footer');
        if (footer) {
          footer.classList.add('keyboard-visible');
        }

        const inputContainer = document.querySelector('.user-chatbox-input-container');
        if (inputContainer) {
          inputContainer.classList.add('keyboard-visible');
        }

        // Scroll to bottom to ensure input is visible
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      };

      const handleBlur = () => {
        // Restore original viewport settings
        viewport.setAttribute('content', originalContent);
        keyboardVisible = false;

        // Remove keyboard-visible class
        const footer = document.querySelector('.user-chatbox-footer');
        if (footer) {
          footer.classList.remove('keyboard-visible');
        }

        const inputContainer = document.querySelector('.user-chatbox-input-container');
        if (inputContainer) {
          inputContainer.classList.remove('keyboard-visible');
        }
      };

      const handleVisualViewportChange = () => {
        if (window.visualViewport) {
          const viewport = window.visualViewport;
          const heightDiff = window.innerHeight - viewport.height;

          if (heightDiff > 150) { // Keyboard is likely visible
            if (!keyboardVisible) {
              handleFocus();
            }
          } else { // Keyboard is likely hidden
            if (keyboardVisible) {
              handleBlur();
            }
          }
        }
      };

      const textarea = textareaRef.current;
      if (textarea) {
        textarea.addEventListener('focus', handleFocus);
        textarea.addEventListener('blur', handleBlur);

        // Handle visual viewport changes for better keyboard detection
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', handleVisualViewportChange);
        }

        return () => {
          textarea.removeEventListener('focus', handleFocus);
          textarea.removeEventListener('blur', handleBlur);
          if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
          }
        };
      }
    }

    // Handle orientation changes
    const handleOrientationChange = () => {
      setTimeout(() => {
        // Adjust chatbox position after orientation change
        const chatbox = document.querySelector('.user-chatbox');
        if (chatbox) {
          chatbox.style.height = window.innerHeight + 'px';
          chatbox.style.width = window.innerWidth + 'px';

          // Update safe area insets dynamically
          updateSafeAreaInsets();
          // Update viewport height
          setVH();

          // Force footer to be visible
          const footer = chatbox.querySelector('.user-chatbox-footer');
          if (footer) {
            footer.style.position = 'relative';
            footer.style.zIndex = '1000';
            footer.style.width = '100%';
          }
        }
      }, 100);
    };

    // Function to update safe area insets dynamically
    const updateSafeAreaInsets = () => {
      const chatbox = document.querySelector('.user-chatbox');
      if (chatbox && window.getComputedStyle) {
        const computedStyle = window.getComputedStyle(document.documentElement);
        const bottomInset = computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0px';
        const leftInset = computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0px';
        const rightInset = computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0px';

        chatbox.style.paddingBottom = `calc(${bottomInset} + 0px)`;
      }
    };

    // Update safe area insets on load and resize
    updateSafeAreaInsets();
    window.addEventListener('resize', updateSafeAreaInsets);

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', updateSafeAreaInsets);
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, [isMobile]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Ensure footer is always visible on mobile
  useEffect(() => {
    if (!isMobile) return;

    const ensureFooterVisibility = () => {
      const footer = document.querySelector('.user-chatbox-footer');
      if (footer) {
        // Force proper positioning
        footer.style.position = 'relative';
        footer.style.zIndex = '1000';
        footer.style.width = '100%';
        footer.style.display = 'block';
      }
    };

    // Check footer visibility periodically
    const interval = setInterval(ensureFooterVisibility, 1000);

    // Also check on scroll
    const handleScroll = () => {
      ensureFooterVisibility();
    };

    const chatboxBody = document.querySelector('.user-chatbox-body');
    if (chatboxBody) {
      chatboxBody.addEventListener('scroll', handleScroll);
    }

    return () => {
      clearInterval(interval);
      if (chatboxBody) {
        chatboxBody.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMobile]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
   
  useEffect(() => {
    if (!user) return;

    const fetchChatHistory = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/history?userId=${user._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch chat history.");
        const data = await response.json();
        setMessages(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchChatHistory();

  }, [user]);

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage(""); // Clear current message when starting reply
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleMessageSend = async () => {
    // ... existing code ...
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;
    
    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress
      
      const token = localStorage.getItem("token");
      const messageText = newMessage.trim();
      
      // Store the message text for the pending message display
      setPendingMessage(messageText);
      
      // Clear message input immediately
      setNewMessage("");
      
      try {
        // Handle files first if present
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
          
          // If there's also a text message, send it separately
          if (messageText !== "") {
            // Don't show instant message here - we'll show the pending UI instead
            await sendTextMessage(messageText);
          }
        } else {
          // Don't show instant message here - we'll show the pending UI instead
          setSendProgress(50);
          await sendTextMessage(messageText);
        }
        setSendProgress(100);
        
        // Reset progress after completion
        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
          setPendingMessage(""); // Reset pending message
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
        setPendingMessage(""); // Reset pending message on error
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  // New function to display message instantly
  const showInstantMessage = (messageText) => {
    const tempId = `temp-${Date.now()}`;
    const instantMessage = {
      _id: tempId,
      user: user ? user.name : "Guest",
      message: messageText,
      sender: "user",
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true // Flag to style differently if needed
    };
    
    // Add message to UI immediately
    setMessages(prevMessages => [...prevMessages, instantMessage]);
    
    // Scroll to bottom immediately
    setTimeout(() => {
      scrollToBottom();
    }, 50);
    
    return tempId;
  };

  const sendTextMessage = async (messageText) => {
    const newMessageObj = {
      user: user ? user.name : "Guest",
      message: messageText,
      sender: "user",
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyingTo ? replyingTo._id : null, // Include replyTo if replying
    };
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/send`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error("Failed to send message.");

      // Get server response with actual message ID
      const data = await response.json();
      
      // Add the message to the messages list
      setMessages(prevMessages => [...prevMessages, data]);
      
      // Clear message input after successful send
      setNewMessage("");
      setReplyingTo(null); // Clear reply state after send
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '35px'; // Set to min-height explicitly
      }
      
      // Let the admin know user is no longer typing
      if (socketRef.current) {
        socketRef.current.emit('typing', {
          userId: user._id,
          isTyping: false
        });
      }
    } catch (error) {
      // Add a failed message
      const failedMessage = {
        ...newMessageObj,
        _id: `failed-${Date.now()}`,
        failed: true
      };
      
      setMessages(prevMessages => [...prevMessages, failedMessage]);
      setError("Failed to send message to server. Please check your connection.");
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Validate file sizes for mobile (limit to 10MB per file)
      const maxSize = 10 * 1024 * 1024; // 10MB
      const validFiles = files.filter(file => {
        if (file.size > maxSize) {
          setError(`File ${file.name} is too large. Maximum size is 10MB.`);
          return false;
        }
        return true;
      });
      
      if (validFiles.length > 0) {
        setSelectedFiles(validFiles);
        setError(''); // Clear any previous errors
      }
      
      // Clear the input value to allow re-selecting the same file
      e.target.value = '';
    }
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    
    // Create unique timestamp for this batch of files
    const batchTimestamp = Date.now();
    
    // Add temporary file messages with pending status
    const tempMessages = selectedFiles.map((file, index) => ({
      _id: `temp-file-${batchTimestamp}-${index}`,
      user: user ? user.name : "Guest",
      message: `File uploaded: ${file.name}`,
      sender: "user",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true
    }));
    
    // Show files as pending in UI with smaller message bubbles
    setMessages(prevMessages => [...prevMessages, ...tempMessages]);
    
    // Scroll to bottom to show pending uploads
    setTimeout(() => {
      scrollToBottom();
    }, 50);
    
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });
    
    formData.append("user", user ? user.name : "Guest");
    formData.append("sender", "user");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : ""); // Include replyTo if replying

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();
      
      // Remove temporary messages and add actual ones
      setMessages(prevMessages => {
        const filteredMessages = prevMessages.filter(msg => !msg._id.startsWith(`temp-file-${batchTimestamp}`));
        return [...filteredMessages, ...data];
      });
      
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      // Mark temporary file messages as failed
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith(`temp-file-${batchTimestamp}`) ? 
          { ...msg, failed: true, pending: false } : msg
        )
      );
      setError(error.message);
    }
  };

  // Format time using the utility function
  const formatTime = (timestamp) => {
    return formatChatTime(timestamp);
  };

  const truncateFileName = (name) => {
    if (name && name.length > 20) {
      return `${name.substring(0, 10)}...${name.substring(name.length - 7)}`;
    }
    return name;
  };

  const handleMouseDown = (e) => {
    if (isMobile) return; // Disable resize on mobile

    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing || isMobile) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const newWidth = Math.max(300, Math.min(800, resizeRef.current.startWidth - deltaX));
    // Invert deltaY to make up drag increase height and down drag decrease height
    const newHeight = Math.max(400, Math.min(800, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    if (isMobile) return;

    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    localStorage.setItem('userChatboxSize', JSON.stringify(chatboxSize));
  };

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem('userChatboxSize');
    if (savedSize) {
      try {
        const parsedSize = JSON.parse(savedSize);
        // Ensure constraints are respected
        const constrainedSize = {
          width: Math.max(300, Math.min(800, parsedSize.width || 380)),
          height: Math.max(400, Math.min(800, parsedSize.height || 500))
        };
        setChatboxSize(constrainedSize);
      } catch (error) {
        console.error('Error parsing saved user chatbox size:', error);
      }
    }
  }, []);

  return (
    <div className="user-chatbox" style={isMobile ? {} : { width: chatboxSize.width, height: chatboxSize.height }}>
      <div className="user-chatbox-header">
        <div className="user-chatbox-user-info">
          <div className="user-chatbox-user-avatar">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="user-chatbox-user-details">
            <h5>Chat with Support</h5>
          </div>
        </div>
        <button onClick={onClose} className="user-close-chatbox-btn">
          <FaTimesCircle />
        </button>
      </div>
      
      <div className="user-chatbox-body">
        {error && <div className="user-chat-error">{error}</div>}
        
        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          
          return (
            <React.Fragment key={index}>
              {showDateSeparator && (
                <div className="chat-date-separator">
                  <span>{formatChatDate(msg.timestamp)}</span>
                </div>
              )}
              <div className={`user-message-container ${msg.sender} ${msg.pending ? 'pending' : ''} ${msg.failed ? 'failed' : ''}`}>
                <div className={`user-message ${msg.sender} ${msg.replyTo ? 'reply' : ''}`}>
                  <div className="user-message-actions">
                    <button onClick={() => handleReply(msg)} className="user-reply-btn" title="Reply to message">
                      <FaReply />
                    </button>
                  </div>
                  <div className="user-message-content">
                    {msg.replyTo && msg.replyTo.user && msg.replyTo.message && (
                      <div className="reply-preview">
                        <small>Replying to {msg.replyTo.user}: {msg.replyTo.message?.substring(0, 50)}...</small>
                      </div>
                    )}
                    {msg.fileUrl ? (
                      <div className="user-message-file">
                        <a
                          href={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat/${msg.fileUrl.split('/').pop()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="user-file-link"
                        >
                          <div className="user-file-preview">
                            <div className="user-file-icon">
                              <FaFile />
                            </div>
                            <div className="user-file-info">
                              <span className="user-file-name">{truncateFileName(msg.originalName)}</span>
                              <span className="user-file-size">Click to download</span>
                            </div>
                          </div>
                        </a>
                      </div>
                    ) : (
                      <p>{msg.message}</p>
                    )}
                    <div className="user-message-meta">
                      <span className="user-message-time">
                        {msg.pending ? 'Sending...' : msg.failed ? 'Failed to send' : formatTime(msg.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        
        {adminTyping && (
          <div className="user-typing-indicator" style={{ display: 'flex', padding: '10px', background: '#e9ecef', borderRadius: '16px', marginBottom: '12px', alignSelf: 'flex-start' }}>
            Admin is typing...
          </div>
        )}
        
        {isSending && (
          <div className="user-message-container user">
            <div className="user-message user">
              <div className="user-message-content">
                <p>{pendingMessage}</p>
                <div className="user-message-meta">
                  <span className="user-message-time">Sending...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="user-chatbox-footer">
        {replyingTo && (
          <div className="user-reply-preview-container">
            <div className="user-reply-preview">
              <small>Replying to: {replyingTo.user} - {replyingTo.message?.substring(0, 100)}...</small>
              <button onClick={cancelReply} className="user-cancel-reply-btn">
                <FaTimesCircle />
              </button>
            </div>
          </div>
        )}
        {selectedFiles.length > 0 && (
          <div className="user-file-preview-container">
            {selectedFiles.map((file, index) => (
              <div key={index} className="user-file-preview1">
                <FaFile className="user-file-icon" />
                <span className="user-file-name">{truncateFileName(file.name)}</span>
                <button 
                  onClick={() => removeSelectedFile(index)} 
                  className="user-remove-file-btn"
                >
                  <FaTimesCircle />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="user-chatbox-input-container">
          <label htmlFor="user-file-upload" className="user-file-upload-label">
            <FaPaperclip />
            <input
              id="user-file-upload"
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
            />
          </label>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            onKeyDown={(e) => {
              // Handle mobile virtual keyboards
              if (isMobile && e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            placeholder="Type your message..."
            className="user-chatbox-input"
            rows="1"
            style={{ resize: 'none', overflow: 'auto', maxHeight: '120px' }}
          />
          <button 
            onClick={handleMessageSend} 
            className="user-send-btn"
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
          >
            <IoMdSend />
          </button>
        </div>
      </div>
      {!isMobile && (
        <div className="user-chatbox-resize-handle" onMouseDown={handleMouseDown}></div>
      )}
    </div>
  );
};

export default UserChatbox;